import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { DomainName } from '../types/models/DomainName';
import type { ApiResponseDomainNameList } from '../types/api/ApiResponseDomainName';

interface UseDomainNamesReturn {
  domainNames: DomainName[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useDomainNames = (): UseDomainNamesReturn => {
  const [domainNames, setDomainNames] = useState<DomainName[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchDomainNames = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/datacenters/domain-names');

      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseDomainNameList;
      if (data.status === 'success' && data.data?.domain_names) {
        setDomainNames(data.data.domain_names);
      } else if (data.status === 'fail' || data.status === 'error') {
        setError(data.message ?? 'Failed to load domain names');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching domain names');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchDomainNames();
    }
  }, [auth.isAuthenticated, fetchDomainNames]);

  return { domainNames, loading, error, refetch: fetchDomainNames };
};
